import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Database, ArrowRight, Wallet, Network, Check, FileText,
  Search, ShieldAlert, GitMerge, TrendingUp,
  AlertTriangle, Fingerprint, Tag, BarChart3
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SEOHead } from '@/components/SEOHead'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'
import CookieConsent from '@/components/CookieConsent'

type ModuleStat = { value: string; label: string }
type ModuleStep = { title: string; desc: string; icon: React.ElementType }
type ModuleAudience = { title: string; desc: string }
type ModuleUseCase = { title: string; scenario: string }

type ModuleData = {
  slug: string
  shortName: string
  name: string
  metaTitle: string
  metaDescription: string
  accentColor: string
  icon: React.ElementType
  tagline: string
  heroParagraph: string
  stats: ModuleStat[]
  overview: string[]
  methodology: ModuleStep[]
  audiences: ModuleAudience[]
  useCases: ModuleUseCase[]
  capabilities: string[]
  ctaText: string
}

const MODULES: Record<string, ModuleData> = {
  breachint: {
    slug: 'breachint',
    shortName: 'BreachINT',
    name: 'Breach Intelligence & Credential Leak Monitoring',
    metaTitle: 'Breach Intelligence & Credential Leak Monitoring | BreachINT — Sycek OSINT',
    metaDescription: 'Search 18B+ breach records and stealer-log infections, score exposure with BTRA (Breach Threat & Risk Assessment), and export an investigative PDF report. BreachINT by Sycek OSINT.',
    accentColor: '#ef4444',
    icon: Database,
    tagline: 'Find out what’s already been exposed — before someone else uses it against you.',
    heroParagraph: 'BreachINT searches 18 billion+ breach records across 47,000+ sources for any email, phone number, username, or domain — then goes further than a lookup table. It correlates every exposed field across every source into one investigation, scores the real-world risk with BTRA, and produces a report built to be read by a human, not just filed away.',
    stats: [
      { value: '18B+', label: 'Breach records indexed' },
      { value: '47,000+', label: 'Breach sources' },
      { value: '4', label: 'BTRA sub-scores' },
      { value: '1', label: 'Exportable PDF per case' },
    ],
    overview: [
      'Most breach-lookup tools stop at "yes, this email was in a breach." BreachINT treats that as the starting point of an investigation, not the end of one. Every matching record is normalized — breach sources report the same field a dozen different ways (Email/email/EmailAddress, Phone/PhoneNumber/Mobile) — and cross-referenced against every other source the same identifier appears in, so an analyst sees the full picture: every password, address, device fingerprint, and linked account, grouped by the breach it came from and flagged when it’s personally identifying information.',
      'Credential exposure is only one signal. BreachINT also pulls in stealer-log intelligence — infections from RedLine, Raccoon, and similar infostealer malware families that harvest saved browser credentials directly off a compromised device. A stealer-log hit tells you something a breach record can’t: which machine was infected, when, and whether the compromised credentials belonged to a personal account or a corporate one — the difference between a nuisance and a supply-chain incident.',
      'All of it rolls up into BTRA — Breach Threat & Risk Assessment — a composite score built from four measured sub-scores: credential exposure (what fraction of found records actually contain a password or hash), attack surface (how many distinct breach sources the identifier appears in), digital footprint (how many distinct data-field types are exposed — addresses, device IDs, government IDs, financial fields), and stealer activity (device-compromise volume). BTRA isn’t a black box — every sub-score and its inputs are shown in the exported report, so a reviewer can see exactly why a case scored the way it did.',
      'Investigations also cross-reference sanctions and PEP watchlists (OpenSanctions), OCCRP Aleph’s investigative-records database, domain exposure via LeakIX, and open web intelligence — and every finding is laid out in a network relationship map computed directly from the underlying entity graph, not a screenshot of a chart that may or may not have rendered correctly. An AI-generated investigative assessment narrates the findings in plain language before the analyst ever opens the evidence tables.',
    ],
    methodology: [
      { title: 'Seed & Search', desc: 'Query by email, phone, username, or domain across 47,000+ breach sources', icon: Search },
      { title: 'Normalize & Correlate', desc: 'Reconcile inconsistent field names across sources into one record set', icon: GitMerge },
      { title: 'Stealer-Log Overlay', desc: 'Cross-reference infostealer malware infections tied to the same identifier', icon: AlertTriangle },
      { title: 'BTRA Scoring', desc: 'Four measured sub-scores roll up into one composite exposure score', icon: TrendingUp },
      { title: 'Investigative Report', desc: 'AI narrative + network graph + evidence tables, export-ready as PDF', icon: FileText },
    ],
    audiences: [
      { title: 'Security & Compliance Teams', desc: 'Monitor executive, employee, and third-party credential exposure before it becomes an incident.' },
      { title: 'Law Enforcement & Investigators', desc: 'Pivot from a single identifier to a full exposure picture with a defensible, source-cited report.' },
      { title: 'Due Diligence Analysts', desc: 'Screen counterparties and subjects for breach and stealer-log exposure alongside sanctions and adverse media.' },
      { title: 'Private Investigators', desc: 'Recover linked accounts, contact details, and device history from a single starting identifier.' },
    ],
    useCases: [
      { title: 'Executive Exposure Monitoring', scenario: 'Continuously check leadership emails against new breach dumps and stealer-log feeds, with BTRA flagging anything that crosses a risk threshold.' },
      { title: 'Third-Party & Vendor Risk', scenario: 'Screen vendor domains and contacts for historical breach exposure before onboarding, documented in an exportable report.' },
      { title: 'Incident Response Triage', scenario: 'When a credential-stuffing attack hits, pull every known exposure for the affected accounts to scope how they were actually compromised.' },
      { title: 'Investigative Case-Building', scenario: 'Start from one email or phone number and let BreachINT surface every linked account, password, and device across every breach it appears in.' },
    ],
    capabilities: [
      '18B+ breach records searchable by email, phone, username, or domain',
      'Stealer-log / infostealer infection intelligence with corporate-vs-personal device flagging',
      'BTRA composite risk scoring with four transparent, source-cited sub-scores',
      'Sanctions, PEP, and OCCRP Aleph investigative-database cross-referencing',
      'Server-rendered network relationship map — built from real entity data, not a screenshot',
      'AI-generated investigative assessment narrative alongside every report',
      'Analyst-controlled selective export — choose exactly which records go in the final PDF',
    ],
    ctaText: 'Start a Breach Search',
  },
  walletint: {
    slug: 'walletint',
    shortName: 'WalletINT',
    name: 'Wallet Intelligence & Crypto AML Screening',
    metaTitle: 'Wallet Intelligence & Crypto AML Screening | WalletINT — Sycek OSINT',
    metaDescription: 'Screen crypto wallets against sanctioned-address blocklists, trace transaction flows across chains, and export a SAR-ready compliance report. WalletINT by Sycek OSINT.',
    accentColor: '#f59e0b',
    icon: Wallet,
    tagline: 'Know where the money came from — and where it’s going — before you touch it.',
    heroParagraph: 'WalletINT screens crypto wallet addresses against sanctioned and blocklisted-address databases, traces transaction flows to see who a wallet has actually moved funds with, and turns the findings into a compliance-ready Suspicious Activity Report — without needing a separate blockchain-analytics subscription.',
    stats: [
      { value: 'Multi-chain', label: 'Address & transaction support' },
      { value: '1-click', label: 'SAR export' },
      { value: 'Graph', label: 'Transaction-flow visualization' },
      { value: 'Linked', label: 'Case tagging' },
    ],
    overview: [
      'A wallet address on its own tells you almost nothing. WalletINT starts by screening the address against sanctioned and blocklisted-wallet databases — the crypto equivalent of an OFAC check — so an obvious hit is flagged immediately. From there, it builds a transaction graph: every counterparty the wallet has sent to or received from, laid out visually so a reviewer can see fund flow patterns — a wallet that only ever transacts with one mixer-adjacent address tells a very different story than one with a broad, organic transaction history.',
      'Wallet-to-real-world attribution comes from a Breadcrumbs integration, which surfaces known associations — exchange deposit addresses, previously flagged entities, and other identity signals — tied to addresses in the transaction graph, instead of leaving every counterparty as an anonymous string of characters. Chain support includes the plumbing needed for Tron-based assets in addition to the more commonly screened chains, since a meaningful share of real-world illicit flow moves through USDT on Tron rather than Bitcoin or Ethereum alone.',
      'Every wallet investigation can be tagged to a case, so a screening result isn’t a one-off lookup that gets lost — it’s attached to the same case record an analyst is already building in the platform’s broader investigation tooling (including a direct bridge into the entity-graph Playground for deeper pivoting). When a finding needs to go to a compliance officer or a regulator, WalletINT exports it as a structured SAR (Suspicious Activity Report), with the blocklist hits, transaction graph, and Breadcrumbs attributions already assembled — not a screenshot, a real document built for filing.',
    ],
    methodology: [
      { title: 'Address Screening', desc: 'Check the wallet against sanctioned and blocklisted-address databases', icon: ShieldAlert },
      { title: 'Transaction Graph', desc: 'Map every counterparty the wallet has sent to or received from', icon: Network },
      { title: 'Breadcrumbs Attribution', desc: 'Surface known real-world associations tied to counterparty addresses', icon: Fingerprint },
      { title: 'Case Linking', desc: 'Tag the investigation to a case, with a direct bridge into Playground', icon: Tag },
      { title: 'SAR Export', desc: 'Generate a structured Suspicious Activity Report ready for filing', icon: FileText },
    ],
    audiences: [
      { title: 'Crypto Exchanges & VASPs', desc: 'Screen deposit and withdrawal addresses against blocklists before funds move.' },
      { title: 'AML & Compliance Teams', desc: 'Turn a flagged wallet into a filed SAR without switching to a separate blockchain-analytics tool.' },
      { title: 'Fraud Investigators', desc: 'Trace where stolen or laundered crypto actually moved, and who the counterparties really are.' },
      { title: 'Financial Institutions', desc: 'Extend existing AML screening workflows to cover crypto-linked customer activity.' },
    ],
    useCases: [
      { title: 'Onboarding Screening', scenario: 'Check a new customer’s declared wallet address against blocklists and prior transaction history before approving them.' },
      { title: 'Suspicious Transfer Investigation', scenario: 'Trace an unusual outbound transfer through its full counterparty graph to see if it terminates at a known-flagged address.' },
      { title: 'SAR Filing', scenario: 'Turn a confirmed suspicious wallet into a complete, source-documented Suspicious Activity Report in one export.' },
      { title: 'Case-Linked Investigation', scenario: 'Tag a wallet screening to an existing case so every finding stays attached as the investigation grows.' },
    ],
    capabilities: [
      'Sanctioned and blocklisted wallet-address screening',
      'Multi-chain transaction-graph visualization, including Tron-based assets',
      'Breadcrumbs-powered real-world attribution for transaction counterparties',
      'Case-linked tagging so wallet findings stay attached to the broader investigation',
      'Direct bridge into the Playground entity-graph tool for deeper pivoting',
      'One-click SAR (Suspicious Activity Report) export for compliance filing',
    ],
    ctaText: 'Screen a Wallet',
  },
  socint: {
    slug: 'socint',
    shortName: 'SOCINT',
    name: 'Social OSINT & Cross-Platform Identity Graph Search',
    metaTitle: 'Social OSINT & Cross-Platform Identity Search | SOCINT — Sycek OSINT',
    metaDescription: 'Search emails, phones, usernames, and domains across social platforms and build a cross-referenced identity graph in one query. SOCINT by Sycek OSINT.',
    accentColor: '#3b82f6',
    icon: Network,
    tagline: 'One identifier in. Every linked account, out.',
    heroParagraph: 'SOCINT takes a single starting point — an email address, phone number, username, or domain — and searches across social platforms and public registries to find every account, alias, and digital identity connected to it, laid out as a single navigable graph instead of a stack of disconnected search results.',
    stats: [
      { value: '4', label: 'Seed identifier types' },
      { value: 'Graph', label: 'Cross-platform identity linking' },
      { value: '1', label: 'Query, every linked account' },
      { value: 'Visual', label: 'Investigation workflow' },
    ],
    overview: [
      'Traditional OSINT work means running the same identifier through a dozen different platform-specific search tools by hand, then manually stitching the results together into a mental model of who owns what. SOCINT collapses that into one query: give it an email, phone number, username, or domain, and it searches across the platforms and registries where that identifier — or an alias clearly connected to it — shows up, and returns the result as a graph rather than a flat list.',
      'The graph structure matters because identity resolution is rarely a straight line. A username on one platform links to an email used to register a domain, which links to a phone number tied to a second, unrelated-looking account — SOCINT surfaces those pivot points explicitly, so an analyst can follow the chain of association instead of re-running searches by hand every time a new lead surfaces. Every node in the graph carries its source and confidence, so findings stay traceable back to where they came from.',
      'SOCINT is built to sit alongside the rest of the platform’s investigation tooling rather than as an isolated lookup — identity graph findings feed directly into the same case and entity-graph workflow used across BreachINT, WalletINT, and the Sycek Profiler, so a cross-platform identity discovered here doesn’t need to be re-entered anywhere else to keep building the investigation.',
    ],
    methodology: [
      { title: 'Seed Identifier', desc: 'Start from an email, phone number, username, or domain', icon: Search },
      { title: 'Cross-Platform Search', desc: 'Query social platforms and public registries for matching or linked accounts', icon: Network },
      { title: 'Alias Resolution', desc: 'Surface pivot points where one identifier connects to another', icon: GitMerge },
      { title: 'Graph Construction', desc: 'Lay out every linked account and identifier as one navigable graph', icon: BarChart3 },
      { title: 'Case Integration', desc: 'Feed findings directly into the shared entity-graph investigation workflow', icon: Tag },
    ],
    audiences: [
      { title: 'Investigators & Analysts', desc: 'Resolve a single identifier into a full cross-platform identity picture in one pass.' },
      { title: 'Trust & Safety Teams', desc: 'Link coordinated or repeat-offender accounts back to a shared identifier.' },
      { title: 'Journalists & Researchers', desc: 'Verify sources and map account networks behind a claim or a campaign.' },
      { title: 'Corporate Security', desc: 'Investigate impersonation, harassment, or insider-threat accounts tied to a known identifier.' },
    ],
    useCases: [
      { title: 'Identity Verification', scenario: 'Confirm that a claimed identity’s email, phone, and social accounts are actually consistent with each other.' },
      { title: 'Sockpuppet & Alias Detection', scenario: 'Trace a suspicious account back to other accounts sharing the same underlying identifier.' },
      { title: 'Missing-Persons Leads', scenario: 'Expand from a last-known email or phone number to active social accounts and recent activity.' },
      { title: 'Source Verification', scenario: 'Check a source’s claimed identity against their actual cross-platform footprint before publication.' },
    ],
    capabilities: [
      'Search by email, phone number, username, or domain',
      'Cross-platform account and alias discovery',
      'Visual identity graph instead of flat search results',
      'Source- and confidence-tagged findings for every linked node',
      'Direct integration with case and entity-graph investigation tooling',
    ],
    ctaText: 'Run an Identity Search',
  },
}

export default function ModulePage({ slug }: { slug: string }) {
  const mod = MODULES[slug]
  if (!mod) return null
  const Icon = mod.icon

  return (
    <>
      <SEOHead
        title={mod.metaTitle}
        description={mod.metaDescription}
        canonical={`/${mod.slug}`}
        jsonLd={{
          '@type': 'SoftwareApplication',
          name: `Sycek ${mod.shortName}`,
          applicationCategory: 'SecurityApplication',
          description: mod.metaDescription,
        }}
      />

      <div className="min-h-screen bg-[#070b0e] text-white">
        <ProductPageNav activePage={mod.shortName} />

        {/* Hero */}
        <section className="relative py-24 overflow-hidden">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `radial-gradient(ellipse at 20% 50%, ${mod.accentColor}40 0%, transparent 60%),
                                radial-gradient(ellipse at 80% 20%, ${mod.accentColor}20 0%, transparent 50%)`,
            }}
          />
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              className="max-w-4xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${mod.accentColor}20`, border: `1px solid ${mod.accentColor}40` }}
                >
                  <Icon className="w-6 h-6" style={{ color: mod.accentColor }} />
                </div>
                <Badge
                  className="text-xs font-medium px-3 py-1"
                  style={{ backgroundColor: `${mod.accentColor}20`, color: mod.accentColor, border: `1px solid ${mod.accentColor}40` }}
                >
                  {mod.shortName}
                </Badge>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 leading-tight text-white">
                {mod.name}
              </h1>
              <p className="text-xl leading-relaxed mb-4" style={{ color: mod.accentColor }}>
                {mod.tagline}
              </p>
              <p className="text-lg text-slate-400 leading-relaxed mb-10 max-w-3xl">
                {mod.heroParagraph}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="font-semibold text-black px-8 py-6 text-base shadow-xl" style={{ backgroundColor: mod.accentColor }}>
                    {mod.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/use-cases">
                  <Button size="lg" variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 px-8 py-6 text-base">
                    See Use Cases
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats strip */}
        <section className="border-t border-white/[0.06] bg-black/30">
          <div className="container mx-auto px-6 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {mod.stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  className="text-center"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: mod.accentColor }}>{stat.value}</div>
                  <div className="text-slate-500 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview — the long-form explanatory copy */}
        <section className="py-20 border-t border-white/[0.06]">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: mod.accentColor }}>How it works</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                What {mod.shortName} actually does
              </h2>
              <div className="space-y-5">
                {mod.overview.map((para, i) => (
                  <p key={i} className="text-slate-400 text-lg leading-relaxed">{para}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Methodology steps */}
        <section className="py-20 border-t border-white/[0.06] bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Methodology</h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">From seed identifier to finished report — every step happens inside {mod.shortName}.</p>
            </div>
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {mod.methodology.map((step, i) => {
                  const StepIcon = step.icon
                  return (
                    <motion.div
                      key={step.title}
                      className="relative flex flex-col items-center text-center"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.1 }}
                    >
                      {i < mod.methodology.length - 1 && (
                        <div className="hidden md:block absolute top-5 left-[calc(50%+24px)] right-0 h-px opacity-20" style={{ backgroundColor: mod.accentColor }} />
                      )}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 relative z-10" style={{ backgroundColor: `${mod.accentColor}20`, border: `1px solid ${mod.accentColor}40` }}>
                        <StepIcon className="w-5 h-5" style={{ color: mod.accentColor }} />
                      </div>
                      <div className="text-xs font-mono font-bold mb-1" style={{ color: mod.accentColor }}>Step {i + 1}</div>
                      <div className="text-white text-sm font-semibold mb-1">{step.title}</div>
                      <div className="text-slate-500 text-xs leading-relaxed">{step.desc}</div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className="py-20 border-t border-white/[0.06]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Who it's for</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {mod.audiences.map((a, i) => (
                <motion.div
                  key={a.title}
                  className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-6 hover:border-white/15 transition-all duration-200"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <h3 className="text-white font-semibold mb-2 text-sm">{a.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{a.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-20 border-t border-white/[0.06] bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Use cases</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
              {mod.useCases.map((uc, i) => (
                <motion.div
                  key={uc.title}
                  className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-7"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="text-xs font-mono uppercase tracking-widest mb-3 font-medium" style={{ color: mod.accentColor }}>
                    Use Case {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{uc.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{uc.scenario}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-20 border-t border-white/[0.06]">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">What's included</h2>
              <div className="space-y-3">
                {mod.capabilities.map((cap, i) => (
                  <motion.div
                    key={i}
                    className="flex items-start gap-3 bg-white/[0.03] border border-white/[0.06] rounded-lg px-5 py-4"
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.06 }}
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${mod.accentColor}25` }}>
                      <Check className="w-3 h-3" style={{ color: mod.accentColor }} />
                    </div>
                    <span className="text-slate-300 text-sm leading-relaxed">{cap}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 border-t border-white/[0.06]">
          <div className="container mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Ready to see what {mod.shortName} finds?</h2>
              <p className="text-slate-400 text-xl mb-10 max-w-2xl mx-auto">Start free — no card required.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register">
                  <Button size="lg" className="font-semibold text-black px-10 py-6 text-lg shadow-2xl" style={{ backgroundColor: mod.accentColor, boxShadow: `0 20px 60px ${mod.accentColor}40` }}>
                    {mod.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/use-cases">
                  <Button size="lg" variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 px-8 py-6 text-base">
                    Explore use cases
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <ProductPageFooter />
      </div>
      <CookieConsent />
    </>
  )
}
